import { FC, useState } from 'react'
import { Button, ConfirmBox } from '@corratech/pylot-ui'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { useDeleteWishlist } from '../Hooks/use-delete-wishlist'
import { ToastType, useUI } from '@corratech/pylot-ui/context'
import type { Wishlist } from '@pylot-data/pylotschema'

export type Props = {
    wishlist: Wishlist
    listIndex: number
}

const SAVED_FOR_LATER_RAW_NAMES = [
    'Saved for later',
    'Guardados para después',
    'wishlist|Saved for later'
]

export const DeleteWishlist: FC<Props> = ({ wishlist, listIndex }) => {
    const { t } = useTranslation(['wishlist'])
    const router = useRouter()
    const [show, setShow] = useState(false)
    const { openToast } = useUI()
    const { deleteWishlist, isLoading } = useDeleteWishlist()
    const { id, name } = wishlist

    const displayName = SAVED_FOR_LATER_RAW_NAMES.some(
        (n) => n.toLowerCase() === (name ?? '').toLowerCase()
    )
        ? t('wishlist|Saved for later')
        : (name ?? '')

    const openConfirm = () => setShow(true)
    const closeConfirm = () => setShow(false)

    const handleClick = async () => {
        closeConfirm()
        try {
            const response = await deleteWishlist({ id: Number(id) })

            if (response?.errors) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }

            if (response?.data?.deleteWishlist?.status) {
                router.push('/account/wishlist') //redirect to default wishlist
                openToast(
                    t(`wishlist|Wish List "{{ name }}" has been deleted.`, {
                        name: displayName
                    }),
                    ToastType.Success
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    //default wishlist cannot be deleted
    if (listIndex === 0) return null

    return (
        <>
            <Button
                loading={isLoading}
                disabled={isLoading}
                variant="link"
                onClick={openConfirm}
                onKeyUp={openConfirm}
                style={{ textTransform: 'capitalize' }}
            >
                {t('wishlist|Delete')}
            </Button>
            <ConfirmBox
                show={show}
                closeConfirm={closeConfirm}
                handleAction={handleClick}
            >
                {t(
                    `wishlist|Are you sure you want to delete your wish list '{{ name }}'? This action can't be undone.`,
                    { name: displayName }
                )}
            </ConfirmBox>
        </>
    )
}
